// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * LE DÉPÔT RÉEL des préférences de tableau (L1, 10/10/2026) : le serveur par
 * le proxy de l'application, le cache par `localStorage`.
 *
 * Le socle est partagé par les trois applications, mais seule la GED expose
 * `/api/ged/ged/pref-tableau/…`. Ailleurs, l'appel échoue et `chargerPref`
 * retombe sur le cache — c'est le même chemin qu'une panne réseau, et il
 * laisse l'écran entièrement fonctionnel. Une application peut remplacer le
 * dépôt par `configurerDepotPrefs` (un autre chemin, une autre session).
 */
import type { DepotPrefs, CacheLocal, ReponsePref } from './tableauPrefs';

const BASE = '/api/ged/ged/pref-tableau/';

async function appel(chemin: string, init?: RequestInit): Promise<Response> {
  return fetch(chemin, { credentials: 'include', signal: AbortSignal.timeout(10_000), ...init });
}

/** Le dépôt par défaut : les routes `/ged/pref-tableau/:id` du moteur. */
export const depotParDefaut: DepotPrefs = {
  async lire(id, colonnes) {
    const q = colonnes.length ? `?colonnes=${encodeURIComponent(colonnes.join(','))}` : '';
    const r = await appel(`${BASE}${encodeURIComponent(id)}${q}`);
    if (!r.ok) throw new Error(`pref-tableau ${r.status}`);
    return (await r.json()) as ReponsePref;
  },
  async ecrire(id, colonnes, ecarts) {
    const r = await appel(`${BASE}${encodeURIComponent(id)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...ecarts, ...(colonnes ? { colonnes } : {}) }),
    });
    if (!r.ok) throw new Error(`pref-tableau ${r.status}`);
  },
  async effacer(id) {
    const r = await appel(`${BASE}${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`pref-tableau ${r.status}`);
  },
  async imposer(id, colonnes, connues) {
    const r = await appel(`${BASE}${encodeURIComponent(id)}/obligatoires`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ colonnes, connues }),
    });
    if (!r.ok) throw new Error(`pref-tableau ${r.status}`);
    const d = (await r.json()) as { obligatoires?: string[] };
    return d.obligatoires ?? [];
  },
};

let depotCourant: DepotPrefs = depotParDefaut;
export function configurerDepotPrefs(d: DepotPrefs): void { depotCourant = d; }
export function depotPrefs(): DepotPrefs { return depotCourant; }

/** `localStorage`, qui peut manquer (SSR) ou refuser (navigation privée) : chaque accès est gardé. */
export const cacheNavigateur: CacheLocal = {
  lire(cle) { try { return typeof window === 'undefined' ? null : localStorage.getItem(cle); } catch { return null; } },
  ecrire(cle, v) { try { localStorage.setItem(cle, v); } catch { /* privé */ } },
  effacer(cle) { try { localStorage.removeItem(cle); } catch { /* privé */ } },
  cles() {
    try {
      if (typeof window === 'undefined') return [];
      return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i) ?? '').filter(Boolean);
    } catch { return []; }
  },
};
